import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Share2 } from "lucide-react";
import Header from "@/components/Header";
import { blogPosts, getPostBySlug, PILLARS } from "@/data/blogPosts";
import { toast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/SEOHead";

/**
 * Article template. Companion to the section-front index in Blog.tsx.
 *
 * Deliberate choices:
 *  - Body copy is solid `text-foreground`, never a muted grey. Grey body text was the
 *    single biggest reason the old template read thin (see website/style-guide.md §2).
 *  - A sticky left rail carries byline, date, section and jump links, so the measure
 *    stays readable without the column hugging the left edge of a wide screen.
 *  - Sources are rendered as a designed, numbered block rather than buried inline.
 *    Citing real dated sources is the thing that separates these pieces from vendor
 *    content, so it gets treated as a feature.
 *  - Figures are line-art illustrations (optional per post), never stock photography.
 */

// "YYYY-MM-DD" parsed bare is UTC midnight, which renders a day early west of
// Greenwich. Anchoring to midday fixes the off-by-one that showed on every post.
const fmtDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/**
 * Author photos, shared with the About page so the same face appears in both places.
 * A real photo does more than look better: it is one of the signals that establishes
 * the author as a real, identifiable person, which is what the Person/sameAs schema
 * below is also for. Authors without an entry fall back to initials.
 */
const AUTHOR_PHOTOS: Record<string, string> = {
  "Sandra Bustos": "/team/sandra-bustos.png",
};

/**
 * Visible author profile links. The Person schema's sameAs already lists these, but
 * answer engines also look for on-page links to the author's credentials.
 */
const AUTHOR_LINKS: Record<string, { label: string; href: string }[]> = {
  "Sandra Bustos": [
    { label: "LinkedIn", href: "https://www.linkedin.com/in/sandrabustos" },
    { label: "Substack", href: "https://meshlysb.substack.com" },
  ],
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const BlogPost = () => {
  const { slug } = useParams();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const pillar = PILLARS.find((p) => p.id === post.pillar);
  const siblings = blogPosts.filter(
    (p) => p.pillar === post.pillar && p.slug !== post.slug
  );
  const next = siblings.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )[0];

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = { title: post.title, text: post.excerpt, url };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied",
          description: "The article link is on your clipboard.",
        });
      }
    } catch {
      // User dismissed the share sheet. Nothing to recover from.
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    articleSection: pillar?.name,
    author: {
      "@type": "Person",
      name: post.author,
      ...(AUTHOR_PHOTOS[post.author]
        ? { image: `https://www.meshly.ai${AUTHOR_PHOTOS[post.author]}` }
        : {}),
      ...(post.author === "Sandra Bustos"
        ? {
            jobTitle: "CEO",
            url: "https://www.meshly.ai/about",
            worksFor: { "@type": "Organization", name: "Meshly", url: "https://www.meshly.ai" },
            sameAs: [
              "https://www.linkedin.com/in/sandrabustos",
              "https://meshlysb.substack.com",
            ],
          }
        : {}),
    },
    publisher: {
      "@type": "Organization",
      name: "Meshly",
      logo: {
        "@type": "ImageObject",
        url: "https://www.meshly.ai/meshly-favicon-blue.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.meshly.ai/blog/${post.slug}`,
    },
    ...(post.heroImage
      ? { image: `https://www.meshly.ai${post.heroImage}` }
      : {}),
    ...(post.sources?.length
      ? {
          citation: post.sources.map((s) => ({
            "@type": "CreativeWork",
            name: s.title,
            publisher: { "@type": "Organization", name: s.publisher },
            ...(s.url ? { url: s.url } : {}),
          })),
        }
      : {}),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonicalUrl={`https://www.meshly.ai/blog/${post.slug}`}
        article={{
          publishedTime: post.date,
          author: post.author,
          tags: [pillar?.name ?? post.category, "AI verification", "revenue integrity"],
        }}
        structuredData={structuredData}
      />
      <Header />

      <div className="mx-auto max-w-5xl px-6 pb-24">
        <Link
          to="/blog"
          className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Articles
        </Link>

        {/* Header */}
        <header className="pt-8">
          {pillar && (
            <span className="font-mono-tech mb-5 block text-[11px] font-semibold uppercase tracking-[0.17em] text-primary">
              {pillar.name}
            </span>
          )}
          <h1 className="mb-5 max-w-[20ch] font-display text-4xl font-semibold leading-[0.99] tracking-[-0.032em] text-foreground md:text-6xl">
            {post.title}
          </h1>
          <p className="mb-8 max-w-[48ch] text-xl leading-snug text-muted-foreground">
            {post.excerpt}
          </p>

          {/*
            The hero is capped rather than full-bleed. At the container's full 1024px
            the illustration overpowered the headline above it and ran much wider than
            the body measure below it. 720px keeps it in proportion with the type.
          */}
          {post.heroImage && (
            <figure className="mb-11 max-w-[720px]">
              <img
                src={post.heroImage}
                alt={post.heroAlt ?? post.title}
                className="w-full bg-white"
                loading="eager"
                decoding="async"
              />
              {post.heroCaption && (
                <figcaption className="mt-3 max-w-[62ch] text-sm leading-relaxed text-muted-foreground">
                  {post.heroCaption}
                </figcaption>
              )}
            </figure>
          )}
        </header>

        <div className="grid gap-14 md:grid-cols-[minmax(170px,220px)_minmax(0,1fr)]">
          {/* Sticky rail */}
          <div className="flex flex-row flex-wrap gap-8 self-start border-b border-border pb-5 md:sticky md:top-8 md:flex-col md:gap-6 md:border-0 md:pb-0">
            <div>
              <span className="font-mono-tech block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                By
              </span>
              <span className="text-sm text-foreground">{post.author}</span>
            </div>
            <div>
              <span className="font-mono-tech block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                Published
              </span>
              <time dateTime={post.date} className="text-sm text-foreground">{fmtDate(post.date)}</time>
            </div>
            {pillar && (
              <div className="md:border-t md:border-border md:pt-5">
                <span className="font-mono-tech block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  In this section
                </span>
                <Link to="/blog" className="text-sm text-primary">
                  {pillar.name}
                </Link>
              </div>
            )}
            {post.sources?.length ? (
              <div className="md:border-t md:border-border md:pt-5">
                <span className="font-mono-tech block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Jump to
                </span>
                <a href="#sources" className="text-sm text-primary">
                  Sources
                </a>
              </div>
            ) : null}
            <div className="md:border-t md:border-border md:pt-5">
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>
          </div>

          {/* Body */}
          <article className="max-w-[66ch]">
            {/*
              Article bodies are authored HTML literals in src/data/blogPosts.ts, compiled
              into the bundle at build time. There is no user-supplied or network-fetched
              content on this path, so this is not an injection surface. If article content
              ever moves to a CMS or any runtime source, sanitize before rendering.
            */}
            <div
              className="article-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {post.substack && (
              <aside className="mt-11 border-l-2 border-primary bg-secondary/40 py-4 pl-5 pr-4">
                <span className="font-mono-tech block text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  Also on Substack
                </span>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground">
                  A longer version of this argument, with the full source trail:{" "}
                  <a
                    href={post.substack.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    {post.substack.title}
                  </a>
                </p>
              </aside>
            )}

            {post.sources?.length ? (
              <div id="sources" className="mt-14 border-t-2 border-foreground pt-6">
                <h2 className="font-mono-tech mb-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Sources
                </h2>
                <ol className="list-none p-0">
                  {post.sources.map((s, i) => (
                    <li
                      key={`${s.publisher}-${i}`}
                      className={`relative py-2.5 pl-8 text-[15px] leading-relaxed text-muted-foreground ${
                        i > 0 ? "border-t border-border" : ""
                      }`}
                    >
                      <span className="font-mono-tech absolute left-0 top-3 text-[11px] font-semibold text-primary">
                        {i + 1}
                      </span>
                      {s.publisher},{" "}
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground underline decoration-border underline-offset-2 hover:decoration-primary"
                        >
                          {s.title}
                        </a>
                      ) : (
                        <span className="text-foreground">{s.title}</span>
                      )}
                      {s.date ? `, ${s.date}` : ""}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            {/* Author */}
            <div className="mt-11 grid grid-cols-[52px_1fr] items-start gap-4 border-t border-border pt-6">
              {AUTHOR_PHOTOS[post.author] ? (
                <img
                  src={AUTHOR_PHOTOS[post.author]}
                  alt={post.author}
                  width={52}
                  height={52}
                  loading="lazy"
                  className="h-[52px] w-[52px] rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-primary font-display text-xl text-white"
                  aria-hidden="true"
                >
                  {initialsOf(post.author)}
                </div>
              )}
              <div>
                <p className="mb-1 text-[15px] font-bold text-foreground">
                  {post.author}
                </p>
                <p className="text-[15px] leading-relaxed text-muted-foreground">
                  CEO of Meshly, building the trust harness for the back office in the
                  agentic era. Systems are connected; the meaning isn&rsquo;t. Meshly
                  catches when your systems disagree and proves what happened, whether a
                  person or an agent did it, starting in finance.
                </p>
                {AUTHOR_LINKS[post.author] ? (
                  <p className="mt-2 flex gap-4 text-sm">
                    {AUTHOR_LINKS[post.author].map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="author noopener noreferrer"
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {link.label}
                      </a>
                    ))}
                  </p>
                ) : null}
              </div>
            </div>

            {next && (
              <div className="mt-10 border-t border-border pt-5">
                <span className="font-mono-tech mb-2.5 block text-[10.5px] uppercase tracking-[0.15em] text-muted-foreground">
                  Next in this section
                </span>
                <Link
                  to={`/blog/${next.slug}`}
                  className="block font-display text-xl font-semibold leading-tight tracking-[-0.015em] text-foreground transition-colors hover:text-primary"
                >
                  {next.title}
                </Link>
              </div>
            )}
          </article>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
